// item 63 — recurring revenue (subscription schedules converted from invoices).
// Mirrors the backend RecurringRevenueIndexItemDto + the two enums.

export enum RecurringRevenueStatus {
  Active = 0,
  Paused = 1,
  Cancelled = 2,
}

export enum RecurrenceFrequency {
  Monthly = 0,
  Quarterly = 1,
  Annual = 2,
  Custom = 3,
}

export interface RecurringRevenue {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  customerId: string;
  customerName: string;
  sourceInvoiceId?: string;
  amount: number;
  frequency: RecurrenceFrequency;
  customIntervalDays?: number;
  status: RecurringRevenueStatus;
  startDate: string;
  nextBillDate: string;
  serviceCategory?: string;
  stripeSubscriptionId?: string;
  // item 706 — latest Stripe payment confirmation (item 581), shown in the ledger STRIPE column
  stripeLastPaymentDate?: string;
  stripeLastPaymentAmount?: number;
  stripeLastInvoiceId?: string;
  // item 887 — latest ISSUED charge (invoice.finalized) so the transaction id shows in the
  // ledger before the ACH payment clears (StripeLastPayment* only stamps at invoice.paid)
  lastChargeIssuedDate?: string;
  lastChargeAmount?: number;
  lastChargeInvoiceNumber?: string;
  lastChargeStripeInvoiceId?: string;
  // item 746 — the enrolled proposal + the client's PO (editable after enrollment)
  proposalId?: string;
  proposalNumber?: string;
  customerPoNumber?: string;
  invoiceHeaderNote?: string | null; // item 1424 — editable invoice memo/footer notes
  invoiceFooterNote?: string | null;
  // item 728 — on auto-pay (charge_automatically) vs. emailed invoices; auto-pay is opt-in
  autoPayEnrolled?: boolean;
  autoPayAuthorizationMemo?: string | null; // item 1341 — the typed authorization note
  lastModifiedDate: string;
  isVoid: boolean;
  isArchive: boolean;
  // item 1360 — what the subscription delivers (RNWY entitlements), human-verified
  isRnwy?: boolean;
  isSwitchPilot?: boolean;
  isHelpDesk?: boolean;
  helpDeskTier?: number; // 0 none · 1 gold · 2 platinum · 3 titanium
  isThirdParty?: boolean;
  productsVerifiedDate?: string | null;
  // item 169 — per-rep monthly commission splits (Outside = PO, Inside = IO)
  outsideRepUserId?: string;
  outsideRepName?: string;
  outsideRepPercent?: number;
  insideRepUserId?: string;
  insideRepName?: string;
  insideRepPercent?: number;
  // item 725 — the monthly line-item breakdown of the amount (RNWY license / SwitchPilot / Help Desk)
  items?: RecurringRevenueLine[];
}

export interface RecurringRevenueLine {
  sku?: string;
  brand?: string;
  description?: string;
  monthlyAmount: number;
}

export interface RecurringRevenuesDto {
  index: RecurringRevenue[];
  length: number;
}

// item 706 — a "not enrolled" subscription: a sold RNWY subscription proposal (2yr/5yr)
// whose project has no live recurring record yet. Listed in the Recurring section's
// needs-enrollment rows; ENROLL opens the enroll panel keyed by proposalId.
export interface RecurringPendingEnrollment {
  proposalId: string;
  proposalNumber: string;
  proposalTitle: string;
  projectId: string;
  projectName: string;
  customerId: string;
  customerName: string;
  saleType: number;
  date: string;
  declined: boolean; // item 731 — a declined enrollment stays listed (reversible)
  // item 756 — the deal hasn't FINAL-billed yet (red "PENDING · NOT FINAL BILLED" stamp;
  // still enrollable early)
  finalBilled?: boolean;
  // item 761 — the monthly value the deal would bill once enrolled (KPI on the subs page)
  monthlyValue?: number;
}

export interface RecurringPendingEnrollmentsDto {
  index: RecurringPendingEnrollment[];
  length: number;
}

// item 64 — normalise a schedule's amount to a monthly figure so schedules of
// different cadences can be summed into one MRR number.
export const monthlyRecurringValue = (
  r: { amount: number; frequency: RecurrenceFrequency; customIntervalDays?: number },
): number => {
  switch (r.frequency) {
    case RecurrenceFrequency.Monthly: return r.amount;
    case RecurrenceFrequency.Quarterly: return r.amount / 3;
    case RecurrenceFrequency.Annual: return r.amount / 12;
    case RecurrenceFrequency.Custom: {
      const days = r.customIntervalDays && r.customIntervalDays > 0 ? r.customIntervalDays : 30;
      return r.amount * (30 / days);
    }
    default: return r.amount;
  }
};

export const frequencyLabel = (f: RecurrenceFrequency, customIntervalDays?: number): string => {
  switch (f) {
    case RecurrenceFrequency.Monthly: return 'Monthly';
    case RecurrenceFrequency.Quarterly: return 'Quarterly';
    case RecurrenceFrequency.Annual: return 'Annual';
    case RecurrenceFrequency.Custom:
      return customIntervalDays ? `Every ${customIntervalDays} days` : 'Custom';
    default: return String(f);
  }
};

// item 1360 — Help Desk tiers (mirrors RnwyHelpDeskTier)
export const HELP_DESK_TIER_LABEL: Record<number, string> = {
  0: 'None', 1: 'Gold', 2: 'Platinum', 3: 'Titanium',
};
export const hasProducts = (r: Pick<RecurringRevenue, 'isRnwy' | 'isSwitchPilot' | 'isHelpDesk' | 'isThirdParty'>): boolean => !!(r.isRnwy || r.isSwitchPilot || r.isHelpDesk || r.isThirdParty);
